export const DISCORD_RATE_LIMITED = 429;

export const DISCORD_AUTHORIZE_URL = "https://discord.com/api/oauth2/authorize";

const DISCORD_CDN_BASE = "https://cdn.discordapp.com";

export const DISCORD_SNOWFLAKE = /^[1-9]\d{16,19}$/;

const SNOWFLAKE_MAX = 2n ** 64n - 1n;

export const isDiscordUserId = (value: string): boolean =>
  DISCORD_SNOWFLAKE.test(value) && BigInt(value) <= SNOWFLAKE_MAX;

export const SNOWFLAKE_MAX_DIGITS = String(SNOWFLAKE_MAX).length;

export const DISCORD_EPOCH_MS = Date.UTC(2015, 0, 1);

export const DISCORD_MAX_ROLES = 250;

export const DISCORD_MAX_CHANNELS = 500;

const DEFAULT_AVATAR_COUNT = 6n;

export const PERMISSION_MANAGE_GUILD = 1n << 5n;

const PERMISSION_VIEW_CHANNEL = 1n << 10n;

export const PERMISSION_SEND_MESSAGES = 1n << 11n;

export const PERMISSION_EMBED_LINKS = 1n << 14n;

export const PERMISSION_ATTACH_FILES = 1n << 15n;

const PERMISSION_READ_MESSAGE_HISTORY = 1n << 16n;

export const PERMISSION_USE_EXTERNAL_EMOJIS = 1n << 18n;

export const PERMISSION_MANAGE_ROLES = 1n << 28n;

export const PERMISSION_MANAGE_THREADS = 1n << 34n;

export const GRANTABLE_PERMISSIONS = {
  view_channel: PERMISSION_VIEW_CHANNEL,
  send_messages: PERMISSION_SEND_MESSAGES,
  send_messages_in_threads: 1n << 38n,
  create_public_threads: 1n << 35n,
  embed_links: PERMISSION_EMBED_LINKS,
  attach_files: PERMISSION_ATTACH_FILES,
  add_reactions: 1n << 6n,
  use_external_emojis: PERMISSION_USE_EXTERNAL_EMOJIS,
  use_external_stickers: 1n << 37n,
  read_message_history: PERMISSION_READ_MESSAGE_HISTORY,
  send_voice_messages: 1n << 46n,
  use_application_commands: 1n << 31n,
  change_nickname: 1n << 26n,
  connect: 1n << 20n,
  speak: 1n << 21n,
  stream: 1n << 9n,
  use_vad: 1n << 25n,
  priority_speaker: 1n << 8n,
  create_guild_expressions: 1n << 43n,
  create_instant_invite: 1n << 0n,
  create_private_threads: 1n << 36n,
  send_tts_messages: 1n << 12n,
  send_polls: 1n << 49n,
  use_soundboard: 1n << 42n,
  use_external_sounds: 1n << 45n,
  set_voice_channel_status: 1n << 48n,
  use_embedded_activities: 1n << 39n,
  use_external_apps: 1n << 50n,
  request_to_speak: 1n << 32n,
  create_events: 1n << 44n,
} as const;

export type GrantablePermission = keyof typeof GRANTABLE_PERMISSIONS;

export const GRANTABLE_PERMISSION_NAMES = Object.keys(
  GRANTABLE_PERMISSIONS,
) as readonly GrantablePermission[];

export const permissionBits = (names: readonly GrantablePermission[]): bigint =>
  names.reduce((bits, name) => bits | GRANTABLE_PERMISSIONS[name], 0n);

export const PERMISSION_ADMINISTRATOR = 1n << 3n;

export const GRANTABLE_BITS = permissionBits(GRANTABLE_PERMISSION_NAMES);

export const permissionsBeyondMembers = (role: bigint, everyone: bigint): bigint =>
  role & ~GRANTABLE_BITS & ~everyone;

export const BOT_INSTALL_PERMISSIONS =
  PERMISSION_ADMINISTRATOR |
  PERMISSION_MANAGE_GUILD |
  PERMISSION_VIEW_CHANNEL |
  PERMISSION_READ_MESSAGE_HISTORY |
  PERMISSION_MANAGE_THREADS;

export const LEVELING_BOT_PERMISSIONS =
  BOT_INSTALL_PERMISSIONS |
  PERMISSION_MANAGE_ROLES |
  PERMISSION_SEND_MESSAGES |
  PERMISSION_EMBED_LINKS |
  PERMISSION_ATTACH_FILES |
  PERMISSION_USE_EXTERNAL_EMOJIS;

export const levelingBotPermissions = (rewards: bigint): bigint =>
  LEVELING_BOT_PERMISSIONS | (rewards & GRANTABLE_BITS);

export const DISCORD_ACCESS_REFUSAL_CODES: readonly number[] = [50_013, 50_001];

export const DISCORD_UNKNOWN_MEMBER = 10_007;

export const DISCORD_UNKNOWN_ROLE = 10_011;

const BOT_INSTALL_SCOPES = ["bot", "applications.commands"] as const;

type InstallReturn = { readonly redirectUri: string; readonly state: string };

export const botInstallUrl = (
  botAppId: string,
  guildId: string,
  permissions: bigint = BOT_INSTALL_PERMISSIONS,
  returnTo: InstallReturn | null = null,
): string => {
  const params = new URLSearchParams({
    client_id: botAppId,
    scope: BOT_INSTALL_SCOPES.join(" "),
    permissions: permissions.toString(),
    guild_id: guildId,
    disable_guild_select: "true",
  });
  if (returnTo !== null) {
    params.set("response_type", "code");
    params.set("redirect_uri", returnTo.redirectUri);
    params.set("state", returnTo.state);
  }
  return `${DISCORD_AUTHORIZE_URL}?${params.toString()}`;
};

export const guildIconUrl = (guildId: string | null, iconHash: string | null): string | null =>
  guildId === null || iconHash === null
    ? null
    : `${DISCORD_CDN_BASE}/icons/${guildId}/${iconHash}.png`;

export const botAvatarUrl = (botUserId: string | null, avatarHash: string | null): string | null =>
  botUserId === null ? null : userAvatarUrl(botUserId, avatarHash);

export const userAvatarUrl = (userId: string, avatarHash: string | null): string => {
  if (avatarHash === null) {
    const index = isDiscordUserId(userId) ? (BigInt(userId) >> 22n) % DEFAULT_AVATAR_COUNT : 0n;
    return `${DISCORD_CDN_BASE}/embed/avatars/${index}.png`;
  }
  const extension = avatarHash.startsWith("a_") ? "gif" : "png";
  return `${DISCORD_CDN_BASE}/avatars/${userId}/${avatarHash}.${extension}?size=128`;
};

export const userBannerUrl = (userId: string, bannerHash: string | null): string | null => {
  if (bannerHash === null) return null;
  const extension = bannerHash.startsWith("a_") ? "gif" : "png";
  return `${DISCORD_CDN_BASE}/banners/${userId}/${bannerHash}.${extension}?size=600`;
};

export const memberAvatarPngUrl = (
  guildId: string,
  userId: string,
  avatars: { readonly member: string | null; readonly user: string | null },
  size = 256,
): string => {
  if (avatars.member !== null) {
    return `${DISCORD_CDN_BASE}/guilds/${guildId}/users/${userId}/avatars/${avatars.member}.png?size=${String(size)}`;
  }
  if (avatars.user !== null) {
    return `${DISCORD_CDN_BASE}/avatars/${userId}/${avatars.user}.png?size=${String(size)}`;
  }
  return userAvatarUrl(userId, null);
};
